import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';

/**
 * 属性的key
 */
export const EFFECT_KEY = 'effect';

/**
 * 效果包含的类型枚举
 */
export enum EEffectType {
    GLOW = 'glow', // 发光
    REFLECTION = 'reflection', // 映像
    SOFTEDGE = 'softEdge', // 柔化边缘
    SHADOW = 'shadow', // 阴影
}

/**
 * 效果操作类
 */
export interface IEffect {
    /**
     * 获取效果值 - 发光
     */
    getGlow(): IGlowModelVal | null
    /**
     * 获取效果值 - 映像
     */
    getReflection(): IReflectionModelVal | null
    /**
     * 获取效果值 - 柔化边缘
     */
    getSoftEdge(): ISoftEdgeModelVal | null
    /**
     * 获取效果值 - 阴影
     */
    getShadow(): IShadowModelVal | null
    /**
     * 获取效果值 - 效果集合
     */
    getEffectCollection(): IEffectModelValCollection | null
    /**
     * 更新效果值 - 发光
     * @param glow 发光值
     * @param onlyChangeUI  是否为实时预览（只更新ui，不保存到sdk）
     */
    updateGlow(glow: IGlowModelVal, onlyChangeUI: boolean): void
    /**
     * 更新效果值 - 映像
     * @param reflection 映像值
     * @param onlyChangeUI  是否为实时预览（只更新ui，不保存到sdk）
     */
    updateReflection(reflection: IReflectionModelVal, onlyChangeUI: boolean): void
    /**
     * 更新效果值 - 柔化边缘
     * @param softEdge 柔化边缘值
     * @param onlyChangeUI 是否为实时预览（只更新ui，不保存到sdk）
     */
    updateSoftEdge(softEdge: ISoftEdgeModelVal, onlyChangeUI: boolean): void
    /**
     * 更新效果值 - 阴影
     * @param shadow 阴影值
     * @param onlyChangeUI 是否为实时预览（只更新ui，不保存到sdk）
     */
    updateShadow(shadow: IShadowModelVal, onlyChangeUI: boolean): void
    /**
     * 更新效果值 - 效果集合
     * @param effectValCollection 效果集合
     * @param onlyChangeUI 是否为实时预览（只更新ui，不保存到sdk）
     */
    updateEffectCollection(effectValCollection: IEffectModelValCollection | null, onlyChangeUI: boolean): void
    /**
     * 刷新 多选图形|组合 合并后的效果值
     * 多选图形、组合的属性是综合所有子元素而来的。基于性能考虑，加入缓存，只在必要时重新获取更新缓存
     */
    refreshMergePropertyValCache(): IEffectModelValCollection | null
}

/**
 * 更新时的入参|获取时返回值  -  效果集合
 */
export interface IEffectModelValCollection {
    glow?: IGlowModelVal | null // 发光
    reflection?: IReflectionModelVal | null // 映像
    softEdge?: ISoftEdgeModelVal | null // 柔化边缘
    shadow?: IShadowModelVal | null // 阴影
}

/**
 * EffectVal的可能类型
 */
export type TEffectVal = IGlowModelVal | IReflectionModelVal | ISoftEdgeModelVal | IShadowModelVal;

/**
 * 更新时的入参|获取时返回值  -  发光
 */
export interface IGlowModelVal {
    color: string // 颜色
    tdColor: ITDColor; // 颜色
    size: number // 大小
    opacity: number // 透明度
}

/**
 * 更新时的入参|获取时返回值  -  映像
 */
export interface IReflectionModelVal {
    algn?: EEffectAlgn; // 效果属性应用的原点位置（有9个方向）
    blurRad: number; // 模糊
    dir: number; // 角度
    dist: number; // 距离
    endOpacity?: number; // 结束位置透明度（目前是固定值）
    rotWithShape?: number; // 是否跟随形状旋转
    size: number; // 大小
    startOpacity: number; // 透明度
    sy: number;
}

/**
 * 更新时的入参|获取时返回值  -  柔化边缘
 */
export interface ISoftEdgeModelVal {
    size: number; // 大小
}

/**
 * 效果属性应用的原点位置
 */
export enum EEffectAlgn {
    TL = 'tl', // 左上
    T = 't', // 上
    TR = 'tr', // 右上
    L = 'l', // 左
    CTR = 'ctr', // 中
    R = 'r', // 右
    BL = 'bl', // 左下
    B = 'b', // 下
    BR = 'br', // 右下
}

/**
 * 更新时的入参|获取时返回值  -  阴影
 */
export interface IShadowModelVal {
    blurRad: number; // 模糊
    color: string; // 颜色
    tdColor: ITDColor; // 颜色
    dir: number; // 角度
    dist: number; // 距离
    isOuter: boolean; // 是否为外阴影
    opacity: number; // 透明度
    size: number; // 大小
    algn?: EEffectAlgn; // 效果属性应用的原点位置（有9个方向）
    sizeX?: number; // 横向大小百分比（只有透视效果sizeX !== sizeY）
    sizeY?: number; // 纵向大小百分比（只有透视效果sizeX !== sizeY， 负数翻转）
    skewX?: number; // 横向倾斜角度（只有透视效果有此字段，正数向左倾斜，负数向右倾斜）
    skewY?: number; // 纵向倾斜角度（只有透视效果有此字段）
    rotWithShape?: number // 是否跟随形状旋转
}

/**
 * 数据更新时的数据整合（sdk需要）
 */
export interface IEffectUpdateRemove {
    update?: {
        style: {
            effect: {
                glow?: IGlowModelVal | null
                reflection?: IReflectionModelVal | null
                softEdge?: ISoftEdgeModelVal | null
                shadow?: IShadowModelVal | null
            } | null
        }
    }
    remove?: {
        style?: {
            effect: {
                glow?: null
                reflection?: null
                softEdge?: null
                shadow?: null | { isOuter: boolean }
            }
        }
    } | null
}
